import { CommonModule } from '@angular/common';
import { Component, TemplateRef, ViewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ClientSideRowModelModule, ColumnApiModule, DateFilterModule, GridApi, GridReadyEvent, ModuleRegistry, NumberFilterModule, PaginationModule, RenderApiModule, RowDragModule, RowSelectionModule, TextFilterModule, TooltipModule, ValidationModule } from 'ag-grid-community';
import { BsModalRef, BsModalService, ModalModule } from 'ngx-bootstrap/modal';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { ConfirmDialogComponent } from '../reusable/confirm-dialog/confirm-dialog.component';
import { ToggleRendererComponent } from '../reusable/toggle-reusable/toggle-reusable.component';
import { ApiService } from '../../services/api.service';
import { CreateResearchStudiesComponent } from './create-research-studies/create-research-studies.component';
import { ResearchStudiesDetailComponent } from './research-studies-detail/research-studies-detail.component';
import { CreateOrganisationComponent } from '../manage-organisation/create-organisation/create-organisation.component';
import { CreateBranchComponent } from '../manage-branches/create-branch/create-branch.component';
import { CreateUserComponent } from '../manage-user/create-user/create-user.component';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  PaginationModule,
  RowSelectionModule,
  RowDragModule,
  TooltipModule,
  ColumnApiModule,
  ValidationModule
]);

@Component({
  selector: 'app-manage-research-studies',
  imports: [CommonModule, AgGridAngular, ModalModule, ToastrModule, ConfirmDialogComponent],
  templateUrl: './manage-research-studies.component.html',
  styleUrl: './manage-research-studies.component.scss',
  providers: [BsModalService]
})
export class ManageResearchStudiesComponent {
  @ViewChild('cellTemplate', { static: true }) cellTemplate!: TemplateRef<any>;
  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;
  @ViewChild(ConfirmDialogComponent) confirmDialog!: ConfirmDialogComponent;
  gridApi!: GridApi;
  modalRef?: BsModalRef;
  selectedRowData: any;
  rowData: any[] = [];
  globalTagNameMap: Record<number, string> = {};

  columnDefs: any = [
    {
      field: 'id', hide: true
    },
    {
      headerName: 'Study Name', field: 'study_name', tooltipField: 'study_name', tooltipValueGetter: (params: any) => params.value || 'No data', flex: 1, sortable: true
    },
    {
      headerName: 'By Whom', field: 'by_whom', tooltipField: 'by_whom', tooltipValueGetter: (params: any) => params.value || 'No data', flex: 1, sortable: true
    },
    {
      headerName: 'Registry Title', field: 'registry_title', tooltipField: 'registry_title', tooltipValueGetter: (params: any) => params.value || 'No data', flex: 1, sortable: true
    },
    {
      headerName: 'Who Can Join', field: 'who_can_join', tooltipField: 'who_can_join', tooltipValueGetter: (params: any) => params.value || 'No data', flex: 1, sortable: true
    },
    {
      headerName: 'Duration Of Study', field: 'duration_of_study', tooltipField: 'duration_of_study', tooltipValueGetter: (params: any) => params.value || 'No data', flex: 1, sortable: true
    },
    {
      headerName: 'Global Tag',
      field: 'global_tag_id',
      cellRenderer: (params: any) => this.renderGlobalTagChip(params.data?.global_tag_id),
      tooltipValueGetter: (params: any) => this.getGlobalTagName(params.data?.global_tag_id) || 'No data',
      flex: 1,
      sortable: true
    },
    {
      headerName: 'Completion Config',
      field: 'completion_config',
      valueFormatter: (params: any) => this.formatCompletionConfig(params.value),
      tooltipValueGetter: (params: any) => this.formatCompletionConfig(params.value) || 'No data',
      flex: 1
    },
    
    {
      headerName: 'Deleted',
      field: 'deleted',
      cellRenderer: ToggleRendererComponent,
      flex: 1,
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: (params: any) => {
        return this.getHtml(params);
      },
      sortable: false,
      filter: false,
      flex: 1
    }
  ];
  modules = [ClientSideRowModelModule, ValidationModule, PaginationModule, RowSelectionModule, RowDragModule, TooltipModule, RenderApiModule, ColumnApiModule];
  pagination = true;
  paginationPageSizeSelector = [5, 10, 25, 50, 100];
  paginationPageSize = 10;

  gridOptions = {
    suppressMovableColumns: false,
    rowDragManaged: false,
    animateRows: true,
    enableBrowserTooltips: true,
  };

  constructor(
    private apiService: ApiService,
    private modalService: BsModalService,
    private toastr: ToastrService,
  ) { }

  ngOnInit(): void {
    this.getGlobalTags();
    this.getResearchStudies();
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridApi.setGridOption('context', { componentParent: this });
    this.gridApi.applyColumnState({
      state: [{ colId: 'id', sort: 'desc' }],
      defaultState: { sort: null },
    });
    //  this.addPageSizeSelectorToFooter();
  }

  editRow(rowData: any) {
    console.log('rowData', rowData);
    this.openModal(rowData);
  }

  ViewRow(rowData: any) {
    this.modalRef = this.modalService.show(ResearchStudiesDetailComponent, {
      class: 'modal-xl my-custom-class',
      initialState: {
        researchStudyData: {
          ...rowData,
          global_tag_name: this.getGlobalTagName(rowData?.global_tag_id),
        },
        parentPage: 'Research Studies',
      }
    });
  }

  deleteRow(rowData: any) {
    this.selectedRowData = rowData;
    this.confirmDialog.openModal(rowData);

  }

  onConfirmDelete() {
    if (!this.selectedRowData) return;
    this.apiService.delete(`research-studies/${this.confirmDialog.rowData.id}`).subscribe({
      next: (res: any) => {
        if (res.statusCode == 409) {
          this.toastr.error(res.message, 'Error');
        }
        else {
          this.toastr.success('Research study deleted successfully!', 'Success');
        }
        this.getResearchStudies();
      },
      error: (err: any) => {
        console.error('Error deleting research study:', err);
        this.toastr.error(err.error?.message || 'Failed to delete research study.', 'Error');
      }
    });
  }

  openModal(rowData = '') {
    this.modalRef = this.modalService.show(CreateResearchStudiesComponent, {
      class: 'modal-lg my-custom-class',
      initialState: {
        researchSudiesData: rowData,
        parentPage: 'Manage Research Studies',
      }
    });
    (this.modalRef.content as CreateResearchStudiesComponent).modalClosed.subscribe(() => {
      console.log('Modal Closed now refresh the list');
      this.getResearchStudies();
    });
  }

  getHtml(params: any) {
    const view = this.cellTemplate.createEmbeddedView({
      rowData: params.data,
    });
    view.detectChanges();
    const div = document.createElement('div');
    if (view.rootNodes.length > 0) {
      div.appendChild(view.rootNodes[0]);
      return div;
    } else {
      console.error('view.rootNodes is empty');
      return '';
    }
  }

  openOrganisationModal() {
    this.modalRef = this.modalService.show(CreateOrganisationComponent, {
      class: 'modal-lg my-custom-class',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: false,
      animated: true,
      initialState: {
        parentPage: 'Manage Research Studies',
      }
    });
  }

  getResearchStudies(): void {
    this.apiService.get('research-studies').subscribe({
      next: (res: any) => {
        this.rowData = Array.isArray(res?.data) ? res.data : [];
        if (this.agGrid) {
          this.agGrid.api.refreshCells({ force: true });
        }
      },
      error: (err) => {
        console.log(err);
      }
    })
  }

  getGlobalTags(): void {
    this.apiService.get('globalTag').subscribe({
      next: (res: any) => {
        const globalTags = Array.isArray(res?.data) ? res.data : [];
        this.globalTagNameMap = globalTags.reduce((acc: Record<number, string>, item: any) => {
          if (typeof item?.id === 'number') {
            acc[item.id] = item?.tag_name || '';
          }
          return acc;
        }, {});

        if (this.agGrid) {
          this.agGrid.api.refreshCells({ force: true });
        }
      },
      error: (err) => {
        console.log(err);
      }
    });
  }

  private getGlobalTagName(globalTagId: number | null | undefined): string {
    if (globalTagId === null || globalTagId === undefined) {
      return '';
    }
    return this.globalTagNameMap[globalTagId] || `${globalTagId}`;
  }

  private renderGlobalTagChip(globalTagId: number | null | undefined): HTMLElement {
    const tagName = this.getGlobalTagName(globalTagId) || '-';
    const chip = document.createElement('span');
    chip.className = 'global-tag-chip';
    chip.textContent = tagName;
    return chip;
  }

  private stripHtml(value: string | null | undefined): string {
    return value ? value.replace(/<[^>]*>/g, '').trim() : '';
  }

  private formatCompletionConfig(value: Record<string, any> | null | undefined): string {
    if (!value || typeof value !== 'object') return '';
    if (Array.isArray(value['documents']) && value['documents'].length > 0) {
      return value['documents'].map((item: any) => item?.name).filter(Boolean).join(', ');
    }
    return JSON.stringify(value);
  }

  openBranchModal() {
    this.modalRef = this.modalService.show(CreateBranchComponent, {
      class: 'modal-lg my-custom-class',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: false,
      animated: true,
      initialState: {
        parentPage: 'Manage Research Studies',
      }
    });
  }

  openUserModal() {
    this.modalRef = this.modalService.show(CreateUserComponent, {
      class: 'modal-lg my-custom-class',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: false,
      animated: true,
      initialState: {
        parentPage: 'Manage Research Studies',
      }
    });
  }

  

   onStatusToggle(rowData: any, newStatus: boolean): void {
    this.apiService.patch(`research-studies/update-status/${rowData.id}`, { deleted: newStatus }).subscribe({
      next: () => {
        this.toastr.success('Research study status updated successfully!', 'Success');
        this.getResearchStudies();
      },
      error: (err) => {
        console.error('Error updating research study status:', err);
        this.toastr.error(err.error?.message || 'Failed to update research study status.', 'Error');
       
        this.getResearchStudies();
      }
    });
  }
}
